import { getPackageOrderSuccessPath } from "../ldsOrderStatus";
import {
  buildAgentDashboardLdsBankTransferSuccessPath,
  logBankTransferSuccessPageRouteTrace,
} from "./resolveAgentDashboardLdsBankTransferSuccess";
import { pickRichestPassengersInfoForPersist } from "./resolveOrderPassengersByRooms";
import { resolveOrderCustomerContact } from "./resolveOrderCustomerContact";

function _truthyFlag(v) {
  return v === true || v === 1 || v === "true" || v === "1";
}

function stampLeadContactOnPassengersInfo(passengersInfo, orderRow, clientsInfo) {
  if (!Array.isArray(passengersInfo) || !passengersInfo.length) return passengersInfo;
  const contact = resolveOrderCustomerContact({
    ...(orderRow && typeof orderRow === "object" ? orderRow : {}),
    passengersinfo: passengersInfo,
    clientsinfo: clientsInfo ?? orderRow?.clientsinfo ?? orderRow?.clientsInfo,
  });
  const room0 = passengersInfo[0] && typeof passengersInfo[0] === "object" ? { ...passengersInfo[0] } : {};
  const adults = Array.isArray(room0.adults) ? room0.adults.slice() : [];
  if (!adults.length) return passengersInfo;
  const lead = adults[0] && typeof adults[0] === "object" ? { ...adults[0] } : {};
  if (contact.phone && !String(lead.cell || lead.phone || "").trim()) {
    lead.cell = contact.phone;
  }
  if (contact.email && !String(lead.email || "").trim()) {
    lead.email = contact.email;
  }
  if (contact.name && !String(lead.firstname || lead.firstName || lead.lastname || lead.lastName || "").trim()) {
    const parts = contact.name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      lead.firstname = lead.firstname || parts[0];
      lead.lastname = lead.lastname || parts.slice(1).join(" ");
    } else if (parts.length === 1) {
      lead.firstname = lead.firstname || parts[0];
    }
  }
  adults[0] = lead;
  room0.adults = adults;
  return [room0, ...passengersInfo.slice(1)];
}

/**
 * Build updateLdsOrder body to mark an existing LDS order as bank transfer (later=true).
 * @param {object|null|undefined} orderRow
 * @param {string|null|undefined} orderId
 * @param {string|number|null|undefined} agentId
 * @param {{ roomsAndPaxInfo?: unknown, clientsInfo?: object|null }} [extras]
 */
export function buildLdsPaymentPageBankTransferUpdateBody(
  orderRow,
  orderId,
  agentId,
  extras = {}
) {
  const row = orderRow && typeof orderRow === "object" ? orderRow : {};
  const oid =
    orderId != null && String(orderId).trim() !== ""
      ? String(orderId).trim()
      : row.id != null
        ? String(row.id)
        : row.ID != null
          ? String(row.ID)
          : null;

  const ourPrice = row.ourprice ?? row.ourPrice ?? null;
  const clientsInfo =
    extras.clientsInfo ?? row.clientsinfo ?? row.clientsInfo ?? null;
  const passengersInfo = stampLeadContactOnPassengersInfo(
    pickRichestPassengersInfoForPersist(row, extras.roomsAndPaxInfo),
    row,
    clientsInfo
  );

  return {
    ourLdsId: oid,
    agent_id: agentId ?? row.agent_id ?? row.agentId ?? null,
    later: "true",
    commission: ourPrice?.commission ?? row.commission ?? null,
    handling_fee:
      ourPrice?.handling_fee ??
      ourPrice?.handleFee ??
      row.handling_fee ??
      null,
    currency: row.currency || "USD",
    deal_id: row.dealid ?? row.dealId ?? row.deal_id ?? null,
    deal_type: row.dealtype ?? row.dealType ?? row.deal_type ?? "FlightsHotel",
    supplierId:
      row.supplierid ??
      row.supplier_id ??
      row.supplierId ??
      extras.supplierId ??
      "NONE",
    passengers_info: passengersInfo,
    clientsinfo: clientsInfo,
    ret_date: row.ret_date ?? row.retDate ?? null,
    dep_date: row.dep_date ?? row.depDate ?? null,
    supplement: row.supplement ?? {},
    ourPrice,
    linkedFlights: row.linkedflights ?? row.linkedFlights ?? null,
    extended: row.extended ?? null,
  };
}

/**
 * @param {object|null|undefined} orderRow
 * @param {object|null|undefined} [httpRes]
 */
export function isLdsPaymentPageBankTransferConfirmed(orderRow, httpRes) {
  const row =
    orderRow && typeof orderRow === "object"
      ? orderRow
      : httpRes?.data && typeof httpRes.data === "object"
        ? httpRes.data
        : null;
  if (!row) return false;
  if (_truthyFlag(row.bank_transfer)) return true;
  if (_truthyFlag(row.success)) return true;
  if (String(row.status || "").toLowerCase() === "ok") return true;
  if (String(row.status || "").toLowerCase() === "success") return true;
  return false;
}

/**
 * Success / pending target after bank transfer on agent dashboard payment page.
 * Prefers dashboard success; falls back to canonical package profile success route.
 * @param {string|null|undefined} orderId
 * @param {{ preferDashboardSuccess?: boolean }} [opts]
 */
export function resolveLdsPaymentPageBankTransferSuccessPath(
  orderId,
  opts = { preferDashboardSuccess: true }
) {
  const id = orderId == null ? "" : String(orderId).trim();
  if (!id) return null;
  if (opts.preferDashboardSuccess !== false) {
    return buildAgentDashboardLdsBankTransferSuccessPath(id);
  }
  return getPackageOrderSuccessPath(id);
}

/**
 * @param {object} payload
 */
export function logBankTransferPaymentPageRedirectTrace(payload) {
  if (typeof import.meta !== "undefined" && import.meta.env?.DEV !== true) return;
  try {
    // eslint-disable-next-line no-console
    console.info("[BANK_TRANSFER_PAYMENT_PAGE_REDIRECT_TRACE]", payload);
  } catch {
    /* ignore */
  }
}

/**
 * Navigate after bank transfer confirm — does not surface LDS proposal errors to the user.
 * @param {{
 *   orderRow?: object|null,
 *   orderId?: string|null,
 *   bookingRef?: string|null,
 *   apiStatus?: string|number|null,
 *   proposal_status?: string|null,
 *   redirect_to_success?: boolean,
 *   currentPath?: string|null,
 *   paymentUrlPresent?: boolean,
 *   navigate: (path: string, opts?: object) => void,
 *   preferDashboardSuccess?: boolean,
 * }} input
 * @returns {boolean} navigated
 */
export function redirectAfterLdsPaymentPageBankTransfer(input = {}) {
  const {
    orderRow = null,
    orderId = null,
    bookingRef = null,
    apiStatus = null,
    proposal_status = null,
    redirect_to_success = true,
    currentPath = null,
    paymentUrlPresent = false,
    navigate,
    preferDashboardSuccess = true,
  } = input;

  const oid =
    orderId ??
    orderRow?.id ??
    orderRow?.ID ??
    null;

  const targetPath = resolveLdsPaymentPageBankTransferSuccessPath(oid, {
    preferDashboardSuccess,
  });

  logBankTransferPaymentPageRedirectTrace({
    orderId: oid,
    bookingRef:
      bookingRef ??
      orderRow?.booking_ref ??
      orderRow?.bookingRef ??
      orderRow?.orderid ??
      null,
    apiStatus,
    bank_transfer: Boolean(
      orderRow?.bank_transfer === true ||
        orderRow?.bank_transfer === 1 ||
        orderRow?.bank_transfer === "true"
    ),
    proposal_status:
      proposal_status ??
      orderRow?.proposal_status ??
      orderRow?.order_response?.proposal_status ??
      orderRow?.order_response?.status ??
      null,
    redirect_to_success,
    currentPath: currentPath ?? (typeof window !== "undefined" ? window.location.pathname : null),
    targetPath,
    paymentUrlPresent,
  });

  if (redirect_to_success === false || !targetPath || typeof navigate !== "function") {
    return false;
  }

  navigate(targetPath, { replace: true });

  logBankTransferSuccessPageRouteTrace({
    orderId: oid,
    currentPath: currentPath ?? (typeof window !== "undefined" ? window.location.pathname : null),
    targetPath,
    isUniqueUrl: Boolean(oid && targetPath?.includes(String(oid))),
    canReloadFromUrl: Boolean(oid && targetPath?.includes(String(oid))),
    buttonAction: "/p/dashboard",
  });

  return true;
}
